/**
 * Web client blocks API — local SQLite when sync has bootstrapped, else remote RPC.
 */
import { getCurrentMemberId } from '../auth/current-member-id';
import { cacheAppSession, readCachedAppSession } from '../auth/session-cache';
import { canUseLocalDatabase, isOnline } from '../connectivity';
import { queryBlock } from '../db/queries';
import type { LocalDatabase } from '../db/types';
import { isMissingBlockError } from '../net/api-error';
import { getSyncEngine } from '../sync/engine';

import { newBlockId } from './ids';
import { setCachedPageDocument } from './page-cache';
import { fetchMemberLocal, fetchMemberLocalById, getLocalMemberId } from './member-local';
import {
  createBlockLocal,
  deleteBlockLocal,
  fetchBlockTreeLocal,
  fetchPageDocumentLocal,
  updateBlockLocal,
} from './store';
import type { BlockRecord, MemberRecord } from './types';
import * as remote from './api.web';

export type { MemberManagement, PendingMember, PageDocument } from './api.web';

async function useLocal(): Promise<boolean> {
  if (canUseLocalDatabase()) {
    return true;
  }
  const engine = getSyncEngine();
  if (!isOnline()) {
    return engine.getDatabase() !== null;
  }
  if (engine.getStatus() !== 'ready') {
    try {
      await engine.start();
    } catch {
      return engine.getDatabase() !== null;
    }
  }
  return canUseLocalDatabase();
}

async function getLocalDb() {
  const engine = getSyncEngine();
  let db = engine.getDatabase();
  if (!db) {
    await engine.start();
    db = engine.getDatabase();
  }
  if (!db) {
    throw new Error('Local database not ready');
  }
  return db;
}

export async function fetchBlockTree(parentId: string | null): Promise<BlockRecord[]> {
  if (await useLocal()) {
    return fetchBlockTreeLocal(parentId);
  }
  return remote.fetchBlockTree(parentId);
}

export async function fetchBlock(id: string): Promise<BlockRecord> {
  const engine = getSyncEngine();
  const openDb = engine.getDatabase();
  if (openDb) {
    const cached = await queryBlock(openDb, id);
    if (cached) {
      return cached;
    }
  }
  if (await useLocal()) {
    const block = await queryBlock(await getLocalDb(), id);
    if (!block) {
      throw new Error('Block not found');
    }
    return block;
  }
  return remote.fetchBlock(id);
}

function cacheMemberSession(member: MemberRecord): void {
  const cached = readCachedAppSession();
  cacheAppSession({
    id: member.id,
    name: member.displayName,
    email: cached?.email ?? `${member.id}@local`,
    image: member.avatarUrl,
  });
}

export async function fetchCurrentMember(): Promise<MemberRecord> {
  if (isOnline()) {
    const member = await remote.fetchCurrentMember();
    cacheMemberSession(member);
    return member;
  }

  const memberId = getCurrentMemberId();
  if (memberId) {
    const member = await fetchMemberLocalById(memberId);
    if (member) {
      return member;
    }
  }

  if (await useLocal()) {
    const member = await fetchMemberLocal();
    if (member) {
      return member;
    }
  }

  throw new Error('Member not available offline');
}

export async function createBlock(
  input: Parameters<typeof remote.createBlock>[0],
): ReturnType<typeof remote.createBlock> {
  const engine = getSyncEngine();
  const openDb = engine.getDatabase();
  if (openDb || (await useLocal())) {
    if (!openDb) {
      await engine.start();
    }
    const memberId = await getLocalMemberId();
    const block = await createBlockLocal({
      id: input.id ?? newBlockId(),
      parentId: input.parentId,
      type: input.type,
      title: input.title,
      content: input.content,
      icon: input.icon,
      position: input.position,
      createdBy: memberId,
    });
    await engine.flushOutbox();
    return block;
  }
  const block = await remote.createBlock(input);
  await mirrorCreatedBlockToLocal(block, input.type);
  return block;
}

/** Keep SQLite in sync when the RPC path was used so database views can load immediately. */
async function mirrorCreatedBlockToLocal(
  block: BlockRecord,
  type?: string,
): Promise<void> {
  try {
    const engine = getSyncEngine();
    let db = engine.getDatabase();
    if (!db && isOnline()) {
      try {
        await engine.start();
      } catch {
        return;
      }
      db = engine.getDatabase();
    }
    if (!db) {
      return;
    }
    const { upsertBlockLocal } = await import('../db/queries');
    await upsertBlockLocal(db, block);
    if (type === 'database') {
      const memberId = block.createdBy ?? (await getLocalMemberId());
      const { ensureDatabaseMetadataLocal } = await import('../databases/create-metadata-local');
      await ensureDatabaseMetadataLocal(block.id, memberId);
    }
  } catch {
    // Mirror is best-effort; sync catch-up will reconcile.
  }
}

export async function updateBlock(
  id: string,
  input: Parameters<typeof remote.updateBlock>[1],
): ReturnType<typeof remote.updateBlock> {
  if (await useLocal()) {
    const memberId = await getLocalMemberId();
    const block = await fetchBlock(id);
    return updateBlockLocal(block, input, memberId);
  }
  return remote.updateBlock(id, input);
}

async function openLocalDatabase(): Promise<LocalDatabase | null> {
  const engine = getSyncEngine();
  const open = engine.getDatabase();
  if (open) {
    return open;
  }
  try {
    return await engine.openLocalDatabase();
  } catch {
    return null;
  }
}

/** Soft-delete a row that is already in the local database. */
async function deleteLocalBlockIfPresent(id: string): Promise<boolean> {
  const db = await openLocalDatabase();
  if (!db) {
    return false;
  }
  const local = await queryBlock(db, id);
  if (!local) {
    return false;
  }
  await deleteBlockLocal(local);
  return true;
}

export async function deleteBlock(
  id: string,
  options?: { remote?: boolean },
): Promise<void> {
  if (await deleteLocalBlockIfPresent(id)) {
    return;
  }
  if (options?.remote === false) {
    return;
  }
  try {
    await remote.deleteBlock(id);
  } catch (err) {
    if (!isMissingBlockError(err)) {
      throw err;
    }
    await deleteLocalBlockIfPresent(id);
  }
}

export async function restoreBlock(id: string): Promise<BlockRecord> {
  if (await useLocal()) {
    const { restoreBlockLocal } = await import('./store');
    return restoreBlockLocal(id);
  }
  return remote.restoreBlock(id);
}

export async function reorderBlocks(
  updates: Parameters<typeof remote.reorderBlocks>[0],
): ReturnType<typeof remote.reorderBlocks> {
  if (await useLocal()) {
    const memberId = await getLocalMemberId();
    const results: BlockRecord[] = [];
    for (const update of updates) {
      const block = await fetchBlock(update.id);
      results.push(await updateBlockLocal(block, { position: update.position }, memberId));
    }
    return results;
  }
  return remote.reorderBlocks(updates);
}

export async function reparentBlock(
  id: string,
  input: Parameters<typeof remote.reparentBlock>[1],
): ReturnType<typeof remote.reparentBlock> {
  if (await useLocal()) {
    const memberId = await getLocalMemberId();
    const block = await fetchBlock(id);
    return updateBlockLocal(
      block,
      { parentId: input.parentId, position: input.position },
      memberId,
    );
  }
  return remote.reparentBlock(id, input);
}

export async function fetchPageDocument(
  pageId: string,
): ReturnType<typeof remote.fetchPageDocument> {
  if (await useLocal()) {
    return fetchPageDocumentLocal(pageId);
  }
  return remote.fetchPageDocument(pageId);
}

export function prefetchPageDocument(pageId: string): void {
  if (!getSyncEngine().getDatabase()) {
    return;
  }
  // Warm the open page from SQLite. Hover must not GET /content: menu rows can
  // exist only in this browser, and a missing server page is a console 404.
  void fetchPageDocumentLocal(pageId)
    .then((document) => {
      setCachedPageDocument(pageId, document);
    })
    .catch(() => {});
}

export async function fetchPageContentBlocks(pageId: string): Promise<BlockRecord[]> {
  const document = await fetchPageDocument(pageId);
  return document.contentBlocks;
}

export async function fetchFamilyRootId(): Promise<string | null> {
  const roots = await fetchBlockTree(null);
  const family = roots.find((b) => b.type === 'page' && b.title === 'Family');
  return family?.id ?? null;
}

export async function fetchLibraryRows(
  filter: Parameters<typeof remote.fetchLibraryRows>[0],
): ReturnType<typeof remote.fetchLibraryRows> {
  return remote.fetchLibraryRows(filter);
}

export async function fetchMembers(): Promise<MemberRecord[]> {
  return remote.fetchMembers();
}

export async function fetchFavorites(): ReturnType<typeof remote.fetchFavorites> {
  return remote.fetchFavorites();
}

export async function starBlock(blockId: string): Promise<void> {
  return remote.starBlock(blockId);
}

export async function unstarBlock(blockId: string): Promise<void> {
  return remote.unstarBlock(blockId);
}

export const fetchLlmPrivatePages = remote.fetchLlmPrivatePages;
export const markLlmPrivateBlock = remote.markLlmPrivateBlock;
export const clearLlmPrivateBlock = remote.clearLlmPrivateBlock;

export const presignUpload = remote.presignUpload;
export const presignDownload = remote.presignDownload;
export async function updateMemberProfile(
  input: { displayName: string },
): ReturnType<typeof remote.updateMemberProfile> {
  return remote.updateMemberProfile(input);
}

export const fetchMemberManagement = remote.fetchMemberManagement;
export const addMemberEmail = remote.addMemberEmail;
export const removePendingMember = remote.removePendingMember;
export const removeMember = remote.removeMember;
